// What each role opens in the console, in one place (12 Oct).
//
// THE RAIL, THE ROUTES AND THE ACTIONS ALL READ THIS, AND NOTHING ELSE DECIDES.
// Before it, eleven `role !== "client_viewer"` lines across the rail, the
// router and four screens each restated a piece of the service's policy, and
// they had drifted: an Editor was offered Unverified emails, the Sponsor page
// and Registration › Theme, which the service refuses them, and a Viewer was
// refused People, which the service has let them read since 11 Oct.
//
// THE OLD OBJECTION TO A CONSOLE-SIDE COPY WAS THAT IT WOULD DRIFT, AND IT DID.
// The answer is not to have no copy, which left a deep link drawing a screen
// the service then refused under its own title. It is to have one copy and tie
// it to the service: every entry below names the calls its screen or action
// cannot work without (`api`), and check.mjs reads the guard on each of those
// handlers in xp/api/src and requires `opens` to be exactly what they admit.
// Where it is not, on purpose, `departs` names the decision that keeps it so,
// and the check requires the departure to still be one.
//
// WHAT THIS DECIDES IS WHAT IS DRAWN, NOT WHO MAY. Every request is still
// answered by a service that re-derives the role from the session, holds an
// Editor to the clients they are given and a Viewer to the shows they are
// granted, and asks for a fresh authenticator code on a publish. The role and
// the export switch here are what the service said at sign-in.
//
// The rulings (Gabe, 11 Oct): an Admin reaches everything. An Editor is Nova
// staff given clients, who builds and publishes for them and gets the Game
// desk, with no Team or Settings. A Viewer is one company's people, reading
// the dashboards, People records and activity of the shows they are granted,
// and exporting only with their export switch on. Everyone has an
// authenticator; a BA floor session is exempt from that on the service, and is
// never a console session (it signs in on /v1/ba/sessions and is drawn by the
// BA app), so the console meets it only as a factorless sign-in, which
// App.tsx sends to the authenticator gate before any of this is read.
//
// TWO DECISIONS GABE HAS NOT MADE YET, TAKEN HERE IN A COMMIT OF THEIR OWN SO
// THEY CAN BE DROPPED (12 Oct). C2: Settings is every role's, since everyone
// now has an authenticator, and shows only the reader's own factor and
// sessions. C1: a Viewer is not offered Registration or Kiosk, or their Theme
// pages, which the service refuses them. And on C1 too (12 Oct, its own pair of
// commits so it can be dropped): an Editor gets Themes, Registration › Theme and
// Kiosk › Theme for the clients they hold, which the service now opens them.

import type { Route } from "../routes/route.js";

/** The three accesses Team gives (149), by the service's own role names. */
export type Access = "admin" | "editor" | "viewer";

const ACCESS_OF: Readonly<Record<string, Access>> = {
  nova_admin: "admin",
  nova_editor: "editor",
  client_viewer: "viewer",
};

/** Who is reading, as the sign-in said. */
export type Reader = {
  readonly role: string;
  /** The export switch at sign-in. Null is unknown, and is drawn as off. */
  readonly canExport: boolean | null;
};

/**
 * Who a page or an action is for.
 *
 * `"export"` is "only with their export switch on", which is how the service
 * answers an Editor and a Viewer on the export (reports/routes.ts,
 * exportRefusal). An Admin is always allowed to export, by ruling, so there is
 * no switch to test for one.
 */
export type Grant = {
  readonly admin: boolean;
  readonly editor: boolean | "export";
  readonly viewer: boolean | "export";
};

/** One call a page or action cannot work without, as xp/api registers it. */
export type ApiUse = {
  /** The route file under xp/api/src. */
  readonly file: string;
  readonly method: "get" | "post" | "put" | "patch" | "delete";
  /** The endpoint exactly as the handler is registered with it. */
  readonly route: string;
};

export type Rule = {
  /** Who it opens for: the route, and the rail and every link to it. */
  readonly opens: Grant;
  /**
   * Who the rail offers it to, where a ruling keeps that narrower than who it
   * opens for. Omitted, the rail offers it to whoever it opens for.
   */
  readonly offered?: Grant;
  /** The calls it cannot work without. `opens` is what all of them admit. */
  readonly api: readonly ApiUse[];
  /**
   * Where `opens` or `offered` departs from what the service admits, on
   * purpose: for which accesses, and the decision it waits on. check.mjs
   * requires every other access to agree with the service exactly, and the
   * named ones to still differ, so a stale note is caught as surely as a drift.
   */
  readonly departs?: { readonly on: readonly Access[]; readonly until: string };
};

const ALL: Grant = { admin: true, editor: true, viewer: true };
const NOVA: Grant = { admin: true, editor: true, viewer: false };
const ADMIN: Grant = { admin: true, editor: false, viewer: false };
const EXPORTERS: Grant = { admin: true, editor: "export", viewer: "export" };

const REPORTS = "reports/routes.ts";
const ADMIN_ROUTES = "admin/routes.ts";
const RIDE = "ride/routes.ts";
const KIOSK = "kiosk/routes.ts";
const PUBLISH = "publish/routes.ts";
const IDENTITY = "identity/routes.ts";
const AUTH = "auth/routes.ts";

/**
 * Every page and action whose audience depends on the role.
 *
 * The pages are the route kinds (and, for Registration, Kiosk and the Game
 * desk, the sub-pages whose service differs from their parent's); the actions
 * are buttons and sections inside a page that call something its page does not.
 */
export const ROLE_ACCESS = {
  // ------------------------------------------------------------- overview
  activations: {
    opens: ALL,
    api: [{ file: REPORTS, method: "get", route: "ACTIVATIONS_ENDPOINT" }],
  },
  /**
   * Add activation, for an Admin and for an Editor (12 Oct). The service opens
   * a show for a client an Editor holds, and the picker reads client-choices,
   * which answers an Editor the clients they hold and nothing else about them.
   */
  addActivation: {
    opens: NOVA,
    api: [
      { file: PUBLISH, method: "post", route: "PUBLISH_ENDPOINTS.createActivation" },
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.clientChoices" },
    ],
  },
  /** Add activation's "New client": the company, its first contact and its processing agreement, each an Admin's. */
  addClient: {
    opens: ADMIN,
    api: [
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.createClient" },
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.createIdentity" },
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.recordAgreement" },
    ],
  },
  clients: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.listClients" }],
  },
  /**
   * A company's Theme page, under Clients. The service answers an Editor a
   * held client's default theme (C1), but the page lives under Clients and
   * reads the Clients directory for the company's name, which is an Admin's;
   * an Editor sets their client's default from the Themes page of its show.
   */
  clientTheme: {
    opens: ADMIN,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.clientThemes" },
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.listClients" },
    ],
  },

  // ------------------------------------------------------------- this show
  dashboard: {
    opens: ALL,
    api: [{ file: REPORTS, method: "get", route: "REPORT_ENDPOINTS.operator" }],
  },
  /** The Dashboard's Site visits, from Plausible through the service. */
  siteVisits: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.siteStats" }],
  },
  /**
   * The show's recorded registration setups, which the Wheel reads to say
   * whether Registration is set up. Nova staff's since an Editor builds
   * Registration (12 Oct): the service answers an Editor for a show of a
   * client they hold.
   */
  registrationSetups: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.recordInstruction" }],
  },
  details: {
    opens: NOVA,
    api: [
      { file: PUBLISH, method: "post", route: "PUBLISH_ENDPOINTS.amendActivation" },
      { file: PUBLISH, method: "post", route: "PUBLISH_ENDPOINTS.closeActivation" },
    ],
  },
  /**
   * Show details' Pause and Resume (C3, 12 Oct): an Admin, and an Editor for a
   * client they hold, on the closure's guard. A Viewer reads the state and is
   * offered no button.
   */
  pauseShow: {
    opens: NOVA,
    api: [{ file: PUBLISH, method: "post", route: "PUBLISH_ENDPOINTS.pauseActivation" }],
  },
  /**
   * A person record's Withhold, Block, Correct and Erase (C4, 12 Oct): an
   * Admin's alone, each behind a fresh authenticator code. An Editor and a
   * Viewer read the record and its history and are offered none of them.
   */
  personActions: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.attendeeActions" }],
  },
  /**
   * The show's Themes page: the library a client may wear, and what the show
   * wears. An Editor's for a client they hold (C1); Nova's own themes they
   * wear and do not change, which the service refuses them.
   */
  themes: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.themes" }],
  },
  /** Data is the export: offered to whoever the service lets take one. */
  data: {
    opens: EXPORTERS,
    api: [{ file: REPORTS, method: "get", route: "REPORT_ENDPOINTS.export" }],
  },
  people: {
    opens: ALL,
    api: [
      { file: REPORTS, method: "get", route: "ATTENDEE_ENDPOINTS.list" },
      { file: REPORTS, method: "get", route: "ATTENDEE_ENDPOINTS.record" },
    ],
  },
  unverified: {
    opens: ADMIN,
    api: [{ file: REPORTS, method: "get", route: "UNVERIFIED_ATTEMPTS_ENDPOINT" }],
  },
  draw: {
    opens: NOVA,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.draw" },
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.draw" },
    ],
  },
  activity: {
    opens: ALL,
    api: [{ file: REPORTS, method: "get", route: "ACTIVITY_ENDPOINT" }],
  },

  // ------------------------------------------------------------------ apps
  /**
   * Registration, an Admin's and an Editor's for the clients they hold (Gabe,
   * 11 Oct; the service from 12 Oct). Its first reads are the client's
   * contacts, the show's recorded setups and its legal draft; it records the
   * last two; and Publish Live signs, behind a fresh code. Not a Viewer's (C1):
   * every read it makes refuses them.
   */
  registration: {
    opens: NOVA,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.listIdentities" },
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.recordInstruction" },
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.legalDraft" },
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.recordInstruction" },
      { file: ADMIN_ROUTES, method: "put", route: "PEOPLE_ENDPOINTS.legalDraft" },
      { file: PUBLISH, method: "post", route: "PUBLISH_ENDPOINTS.publishBundle" },
    ],
  },
  /** Registration's Add person, beside the approver picker: a client's contact is put on the books on Team's terms, an Admin's. */
  addContact: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.createIdentity" }],
  },
  /**
   * Registration › Emails (160, decision C7): the passcode's words, the
   * confirmation, the follow-ups and their test sends, on Registration's
   * terms. A Viewer reads what happened to the mail on the Dashboard.
   */
  registrationEmails: {
    opens: NOVA,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.showEmails" },
      { file: ADMIN_ROUTES, method: "put", route: "PEOPLE_ENDPOINTS.showEmails" },
      { file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.showEmailTests" },
    ],
  },
  /** What the registration pages wear: an Editor's for a client they hold (C1). */
  registrationTheme: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.activationThemes" }],
  },
  wheel: {
    opens: NOVA,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.wheel" },
      { file: ADMIN_ROUTES, method: "put", route: "PEOPLE_ENDPOINTS.wheel" },
    ],
  },
  /** Wheel › Prizes (156, C10): weights and stock, an Admin's and a holding Editor's. */
  wheelPrizes: {
    opens: NOVA,
    api: [
      { file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.wheelPrizes" },
      { file: ADMIN_ROUTES, method: "put", route: "PEOPLE_ENDPOINTS.wheelPrizes" },
    ],
  },
  /** Wheel › Results: every spin, read by whoever reads People, a Viewer for a granted show. */
  wheelResults: {
    opens: ALL,
    api: [{ file: REPORTS, method: "get", route: "WHEEL_RESULTS_ENDPOINT" }],
  },
  /** Results' Mark collected: an Admin's and a holding Editor's, as Prizes is. */
  wheelCollect: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "post", route: "PEOPLE_ENDPOINTS.wheelCollections" }],
  },
  /** Results' Export: the export under the wheel_results purpose, so the export's switch. */
  wheelExport: {
    opens: EXPORTERS,
    api: [{ file: REPORTS, method: "get", route: "REPORT_ENDPOINTS.export" }],
  },
  /**
   * Kiosk › Schedule and Content: the playlist, its save, and Publish Live.
   * Not a Viewer's (C1). Since 12 Oct the service answers a Viewer the
   * playlist of a granted show, read only, and saves none for them; Publish
   * Live, the page's point, refuses them.
   */
  kiosk: {
    opens: NOVA,
    api: [
      { file: KIOSK, method: "get", route: "KIOSK_ENDPOINTS.playlist" },
      { file: KIOSK, method: "put", route: "KIOSK_ENDPOINTS.playlist" },
      { file: KIOSK, method: "post", route: "KIOSK_ENDPOINTS.publishSchedule" },
    ],
  },
  kioskScreens: {
    opens: NOVA,
    api: [
      { file: KIOSK, method: "get", route: "KIOSK_ENDPOINTS.seenScreens" },
      { file: KIOSK, method: "post", route: "KIOSK_ENDPOINTS.issueScreenToken" },
    ],
  },
  /** Kiosk › Devices, and the older /tablets address for it. */
  kioskDevices: {
    opens: NOVA,
    api: [{ file: IDENTITY, method: "post", route: "ADMIN_ENDPOINTS.issueEnrolmentCode" }],
  },
  /** What the hall screens wear around the content: an Editor's for a client they hold (C1). */
  kioskTheme: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.activationThemes" }],
  },
  /**
   * Board › Setup (157, decision C11): the show's one board record, which the
   * kiosk's leaderboard piece reads too. Read by whoever reads the kiosk
   * playlist, a Viewer included for a granted show (Gabe, 13 Oct: Viewers
   * read the board), so the page opens for every role and a Viewer is shown
   * it read only.
   */
  board: {
    opens: ALL,
    api: [{ file: KIOSK, method: "get", route: "KIOSK_ENDPOINTS.board" }],
  },
  /** Board › Setup's Save and Reset now: an Admin's, and an Editor's for a client they hold. */
  boardEdit: {
    opens: NOVA,
    api: [
      { file: KIOSK, method: "put", route: "KIOSK_ENDPOINTS.board" },
      { file: KIOSK, method: "post", route: "KIOSK_ENDPOINTS.boardClear" },
    ],
  },
  /** What the board wears, on the shared SurfaceTheme: an Editor's for a client they hold (C1). */
  boardTheme: {
    opens: NOVA,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.activationThemes" }],
  },
  /** The Game desk: All players, Leaderboard, Activity and Mail. */
  game: {
    opens: NOVA,
    api: [
      { file: RIDE, method: "get", route: "RIDE_ENDPOINTS.listRiders" },
      { file: RIDE, method: "get", route: "RIDE_ENDPOINTS.places" },
      { file: RIDE, method: "get", route: "RIDE_ENDPOINTS.listActivity" },
    ],
  },
  /** The Leaderboard's arcade board: the game's own, across every show. */
  arcadeBoard: {
    opens: ADMIN,
    api: [{ file: RIDE, method: "get", route: "RIDE_ENDPOINTS.board" }],
  },
  gameSponsor: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.rideSponsor" }],
  },

  // --------------------------------------------------------------- account
  /**
   * Account, Sign-in security and Notifications, for every role (C2): since
   * #202 everybody has an authenticator, and every role's own factor and
   * sessions are theirs to read and end. Nothing on these pages is anybody
   * else's; the sending figures on Account are `emailStats`, an Admin's.
   */
  settings: {
    opens: ALL,
    api: [{ file: AUTH, method: "post", route: "AUTH_ENDPOINTS.listSessions" }],
  },
  /** Account's sending figures, Mailgun and Resend. */
  emailStats: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.emailStats" }],
  },
  team: {
    opens: ADMIN,
    api: [{ file: ADMIN_ROUTES, method: "get", route: "PEOPLE_ENDPOINTS.team" }],
  },
} as const satisfies Readonly<Record<string, Rule>>;

export type Capability = keyof typeof ROLE_ACCESS;

/**
 * The page a route draws, as this map names it, or null for the two that are
 * not anybody's to open or close: a path the console does not have, and the
 * sign-in form (which a signed-in reader is shown the activations in place of).
 */
export function pageOf(route: Route): Capability | null {
  switch (route.kind) {
    case "sign_in":
    case "choose":
      return "activations";
    case "not_found":
      return null;
    case "clients":
    case "client":
      return "clients";
    case "client_theme":
      return "clientTheme";
    case "team":
      return "team";
    case "account":
    case "enrol":
    case "notices":
      return "settings";
    case "report":
      return route.report === "export" ? "data" : "dashboard";
    case "details":
      return "details";
    case "themes":
      return "themes";
    case "people":
      return "people";
    case "unverified":
      return "unverified";
    case "draw":
      return "draw";
    case "wheel":
      return route.panel === "prizes" ? "wheelPrizes" : route.panel === "results" ? "wheelResults" : "wheel";
    case "activity":
      return "activity";
    case "registration":
      return route.panel === "theme" ? "registrationTheme" : route.panel === "emails" ? "registrationEmails" : "registration";
    case "kiosk":
      return route.panel === "theme"
        ? "kioskTheme"
        : route.panel === "devices"
          ? "kioskDevices"
          : route.panel === "screens"
            ? "kioskScreens"
            : "kiosk";
    case "tablets":
      return "kioskDevices";
    case "ride":
      return route.panel === "sponsor" ? "gameSponsor" : "game";
    case "board":
      return route.panel === "theme" ? "boardTheme" : "board";
  }
}

/** The access a role is, or null for a role this build does not know. */
export function accessOf(role: string): Access | null {
  return ACCESS_OF[role] ?? null;
}

/** "Admin", "Editor", "Viewer": the Team screen's names for the three. */
export function roleName(role: string): string | null {
  const access = accessOf(role);
  return access === "admin" ? "Admin" : access === "editor" ? "Editor" : access === "viewer" ? "Viewer" : null;
}

function granted(grant: Grant, reader: Reader): boolean {
  const access = accessOf(reader.role);
  // A role this build does not know opens nothing. The service's authenticator
  // refuses any role but the three with `role_not_permitted`, so a page drawn
  // for one would be a page of refusals.
  if (access === null) return false;
  const says = grant[access];
  return says === "export" ? reader.canExport === true : says;
}

/** Whether this reader's route opens the page, or the action is drawn. */
export function opens(reader: Reader, capability: Capability): boolean {
  return granted(ROLE_ACCESS[capability].opens, reader);
}

/** Whether the rail and the page's links offer it to this reader. */
export function offers(reader: Reader, capability: Capability): boolean {
  const rule: Rule = ROLE_ACCESS[capability];
  return opens(reader, capability) && granted(rule.offered ?? rule.opens, reader);
}
